import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { Z_INDICES } from '../lib/zIndices';
import { clearAuthSession } from '../utils/authStorage';
import { clearLastActiveRole } from '../utils/lastActiveRole';

/**
 * BlankScreenRecovery — global watchdog that rescues users from stuck
 * blank-screen states (opacity 0 / zero-height route wrapper / crashed
 * child boundary that failed to render fallback UI).
 *
 * WHY THIS EXISTS
 * ---------------
 * The route wrapper in `App.js` is animated by framer-motion. On some
 * Android WebView versions the renderer occasionally freezes at
 * `opacity: 0` and never fires the follow-up frame that would tick it
 * to `1`. We added an explicit `style={{ opacity: 1 }}` belt-and-braces
 * fix, but there are OTHER paths to a blank screen too:
 *   - A protected route bounces the user to `/login` but a stale JWT
 *     puts them in a redirect loop that never renders.
 *   - A lazy chunk load fails silently (network flap).
 *   - A component throws AFTER the initial mount but before any DOM
 *     was written.
 * Rather than chase every source, we ship this watchdog: after 6s on
 * any route, if the page STILL has effectively no visible text we
 * surface a friendly overlay letting the user force-reset their
 * session and jump back to /login.
 *
 * HEURISTIC
 * ---------
 * We check the entire visible body innerText length + count of visible
 * interactive elements. A healthy page has hundreds of characters and
 * multiple buttons/links by 6s. A frozen page has almost nothing (the
 * AdminReturnPill and this watchdog itself are excluded via testids).
 */

// Routes that legitimately render very little text or defer rendering
// (splash, biometric prompt, OAuth handoff, etc). Never trigger on these.
const SKIP_EXACT = new Set([
  '/',
  '/welcome',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/two-factor',
  '/unlock',
  '/google/auth-callback',
  '/google/complete',
  '/app-google-login',
  '/auth/google/callback',
  '/app-login-complete',
  '/payment-complete',
  '/guest/payment-method/callback',
]);
const SKIP_PREFIXES = ['/invite', '/host-invite', '/venue-invite', '/staff-invite'];

const CHECK_DELAY_MS = 6000;

function isSkippedPath(pathname) {
  if (!pathname) return true;
  if (SKIP_EXACT.has(pathname)) return true;
  return SKIP_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function looksBlank() {
  try {
    // Ignore the pill + this overlay themselves — they don't count as content.
    const bodyText = (document.body?.innerText || '').trim();
    const strippedLen = bodyText
      .replace(/Admin/gi, '')
      .replace(/Switching…/gi, '')
      .replace(/Something's stuck.*/gi, '')
      .trim()
      .length;
    if (strippedLen > 40) return false;

    // Count interactive visible elements outside the pill/overlay.
    const interactive = document.querySelectorAll(
      'button:not([data-testid="admin-return-pill"]):not([data-testid="blank-screen-recovery-reset-btn"]), a[href], input, [role="button"]'
    );
    let visibleCount = 0;
    interactive.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) visibleCount += 1;
    });
    if (visibleCount >= 2) return false;

    return true;
  } catch {
    // If we can't measure, assume the page is fine — never falsely trigger.
    return false;
  }
}

export default function BlankScreenRecovery() {
  const location = useLocation();
  const [stuck, setStuck] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // New route → hide any previous overlay and re-arm the timer.
    setStuck(false);
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (isSkippedPath(location.pathname)) return undefined;

    timerRef.current = window.setTimeout(() => {
      if (looksBlank()) {
        setStuck(true);
      }
    }, CHECK_DELAY_MS);

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [location.pathname]);

  const handleReset = () => {
    try {
      clearAuthSession();
    } catch { /* noop */ }
    try {
      // Wipe the remembered role/path too — if the user was stuck on a
      // page they no longer have access to, keeping the memory would
      // send the NEXT login straight back into the same redirect loop
      // (Feb 2026: admin ends up at /guest/… then a non-admin Google
      // account signs in and re-inherits that path → blank forever).
      clearLastActiveRole();
    } catch { /* noop */ }
    try {
      window.sessionStorage.removeItem('allure_session_alive_v1');
    } catch { /* noop */ }
    // Full reload so every React tree, provider, and cached lazy chunk
    // starts fresh at /login.
    window.location.href = '/login';
  };

  if (!stuck) return null;

  return (
    <div
      data-testid="blank-screen-recovery-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: Z_INDICES?.modalOverlay ? Z_INDICES.modalOverlay + 10 : 10000,
        background: 'rgba(15, 17, 21, 0.86)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          background: '#FFFFFF',
          borderRadius: 18,
          padding: '26px 22px',
          maxWidth: 340,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        }}
      >
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 999,
            background: 'rgba(200, 16, 46, 0.10)',
            color: '#C8102E',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 14,
          }}
        >
          <AlertTriangle size={26} strokeWidth={1.8} />
        </div>
        <h3
          style={{
            fontFamily: "'Inria Serif', serif",
            fontSize: 20,
            fontWeight: 700,
            color: '#0F1115',
            margin: '0 0 8px',
          }}
        >
          Something&apos;s stuck
        </h3>
        <p
          style={{
            fontFamily: "'Manrope', sans-serif",
            fontSize: 14,
            color: '#4B4F58',
            lineHeight: 1.55,
            margin: '0 0 18px',
          }}
        >
          This screen didn&apos;t load properly. Tap below to sign out and
          reset — you can sign back in straight away.
        </p>
        <button
          type="button"
          data-testid="blank-screen-recovery-reset-btn"
          onClick={handleReset}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '11px 22px',
            borderRadius: 9999,
            background: '#0F1115',
            color: '#FFFFFF',
            border: 'none',
            fontFamily: "'Manrope', sans-serif",
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          <RotateCw size={15} strokeWidth={2.2} />
          Sign out and reset
        </button>
      </div>
    </div>
  );
}
